import React, { useState, useRef, useEffect } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { searchItems } from '../../utils/search/searchEngine';
import { SearchResult } from '../../utils/search/types';
import SearchResults from './SearchResults';
import { useClickOutside } from '../../hooks/useClickOutside';
import { jobsApi } from '../../api/jobs';
import { candidatesApi } from '../../api/candidates';
import { updateSearchCache } from '../../utils/search/searchItems';
import { useLanguage } from '../../contexts/LanguageContext';

const GlobalSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const { t } = useLanguage();

  useClickOutside(containerRef, () => setIsOpen(false));

  // Initialize search data
  useEffect(() => {
    const initializeSearchData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch data from APIs
        const [jobs, candidates] = await Promise.all([
          jobsApi.getAll(),
          candidatesApi.getAll()
        ]);
        
        // Collect all references from candidates
        const allReferences = candidates.reduce((refs, candidate) => {
          if (candidate.references && candidate.references.length > 0) {
            return [...refs, ...candidate.references];
          }
          return refs;
        }, [] as any[]);
        
        // Update search cache with fetched data
        updateSearchCache(jobs, candidates, allReferences);
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize search data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeSearchData();
  }, []);

  // Handle search query
  useEffect(() => {
    // Clear any existing timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (query.trim().length >= 3) {
      // Set a small delay to avoid searching on every keystroke
      searchTimeout.current = setTimeout(() => {
        const searchResults = searchItems(query);
        setResults(searchResults);
        setIsOpen(true);
        searchTimeout.current = null;
      }, 300);
    } else {
      setResults([]);
      setIsOpen(false);
    }

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [query]);

  const handleClose = () => {
    setQuery('');
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative w-[600px]">
      <div className="relative">
        {isLoading ? (
          <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 animate-spin" />
        ) : (
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
        )}
        <input
          type="text"
          placeholder={t('search.placeholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-gray-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          onFocus={() => query.trim().length >= 3 && setIsOpen(true)}
          disabled={!isInitialized || isLoading}
        />
      </div>

      {isOpen && (
        <SearchResults 
          results={results} 
          onSelect={handleClose}
        />
      )}
    </div>
  );
};

export default GlobalSearch;